import AssistantPage from '@modules/assistant';
import React, { ReactElement } from 'react';
import { IsAuthenticatedProvider } from '@modules/assistant/context/IsAuthenticatedProvider';
import {
  availableCreatorHubLocales,
  defaultLocale,
} from '@modules/localization/constants/translationResourceConstants';
import TranslationNamespace from '@modules/localization/enums/TranslationNamespace';
import {
  loadPageLayoutTranslationResources,
  loadTranslationResources,
  translationResourceProvider,
} from '@modules/localization/utils/translationResourceUtils';
import MultiProvider from '@modules/miscellaneous/common/components/MultiProvider';
import { Locale, LocalizationProvider } from '@rbx/intl';
import { GetStaticProps } from 'next';
import { AssistantPageProps, TranslationResourcesByLocale } from 'types';
import Head from 'next/head';
import { NavigationConfigsProvider } from '@rbx/creator-hub-navigation';
import Navigation from '@modules/utilities/interfaces/Navigation';
import { join } from 'path';
import PageTrackingProvider from '@modules/eventStream/providers/PageTrackingProvider';
import CurrentLocaleProvider from '@modules/localization/implementations/CurrentLocaleProvider';

export default AssistantPage;

AssistantPage.getPageLayout = (page: ReactElement, props: AssistantPageProps) => {
  const { title, metaTags = [] } = props;
  return (
    <MultiProvider
      providers={[
        <IsAuthenticatedProvider key='IsAuthenticatedProvider' />,
        <CurrentLocaleProvider key='CurrentLocaleProvider' />,
        <LocalizationProvider key='LocalizationProvider' provider={translationResourceProvider} />,
        <PageTrackingProvider key='PageTrackingProvider' pageProps={props} />,
        <NavigationConfigsProvider
          key='NavigationConfigsProvider'
          currentProduct='Assistant'
          environment={process.env.environment}
          target='global'
        />,
      ]}>
      {title && (
        <Head>
          <title>{title}</title>
        </Head>
      )}
      {metaTags && (
        <Head>
          {metaTags.map((tagProps: Record<string, string>) => (
            <meta key={tagProps.name} {...tagProps} />
          ))}
        </Head>
      )}
      {page}
    </MultiProvider>
  );
};

const assistantTranslationNamespaces: TranslationNamespace[] = [TranslationNamespace.Assistant];

const loadResources = async (
  translationResourcesByLocale: TranslationResourcesByLocale,
  locale: Locale,
) => {
  const resources = await Promise.all([
    loadTranslationResources(assistantTranslationNamespaces, locale),
    loadPageLayoutTranslationResources(locale),
  ]);
  // eslint-disable-next-line no-param-reassign
  translationResourcesByLocale[locale] = resources.flat();
};

const getLocalizationProps = async () => {
  const translationResourcesByLocale: TranslationResourcesByLocale = {};
  const defaultTranslationResourcesLocale = defaultLocale;
  return Promise.all(
    availableCreatorHubLocales.map((locale) => loadResources(translationResourcesByLocale, locale)),
  ).then(() => ({
    translationResourcesByLocale,
    defaultTranslationResourcesLocale,
    defaultTranslationResources:
      translationResourcesByLocale[defaultTranslationResourcesLocale] ?? [],
  }));
};

const getNavigationProps = async () => {
  const navigation: Navigation = {
    navigationContent: [],
  };
  return {
    navigation,
    urlBasePath: '/',
  };
};

export const getStaticProps: GetStaticProps<AssistantPageProps> = async () => {
  const localizationProps = await getLocalizationProps();
  const navigationProps = await getNavigationProps();
  const siteTitle = 'Assistant | Documentation';
  const sharePostTitle = 'Roblox Assistant for Documentation'; // max 70 characters
  const seoDescription =
    'Use our AI assistant to help you build, grow, and monetize your Roblox creations.';
  const shareImageLink = join(process.env.assetPathPrefix, 'docs_assistant_share.png');
  const availableLocalesMetaTags = availableCreatorHubLocales.map((locale) => ({
    property: 'og:locale:alternate',
    content: String(locale),
  }));
  const keywords = [
    'ai chat',
    'roblox ai chat',
    'ai',
    'roblox ai',
    'chat',
    'roblox chat',
    'chatai',
    'roblox chatai',
    'chatbot',
    'roblox chatbot',
    'assistant',
    'roblox assistant',
    'ai assistant',
    'roblox ai assistant',
    'chatapp',
    'roblox chatapp',
    'chat app',
    'roblox chat app',
  ].join(',');
  return {
    props: {
      ...localizationProps,
      ...navigationProps,
      title: siteTitle,
      metaTags: [
        { property: 'og:url', content: 'https://create.roblox.com/docs/assistant' },
        { property: 'og:site_name', content: 'Roblox Creator Hub' },

        { property: 'og:type', content: 'website' },
        { name: 'twitter:card', content: 'summary_large_image' },

        { property: 'og:title', content: sharePostTitle },
        { property: 'twitter:title', content: sharePostTitle },

        { name: 'description', content: seoDescription },
        { property: 'og:description', content: seoDescription },
        { property: 'twitter:description', content: seoDescription },

        { property: 'twitter:image', content: shareImageLink },
        { name: 'og:image', content: shareImageLink },

        { name: 'google', content: 'nositelinkssearchbox' },
        { httpEquiv: 'Content-Type', content: 'text/html; charset=utf-8' },
        { name: 'robots', content: 'index, follow' },
        {
          name: 'keywords',
          content: keywords,
        },
        ...availableLocalesMetaTags,
      ] as Record<string, string>[],
    },
  };
};
